import React, {useCallback, useEffect, useRef, useState} from 'react';

import {addToast, Button, Icon, LoadingSpinner, Menu, Text, Toggle} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import {getClassesQuery} from 'client/Assignments/GlobalAssignments/GlobalAssignments.queries';
import sessionStore from 'client/Session/SessionStore';
import {normalizeError} from 'lib/normalizeError';

import type {SelectedClassrooms} from '../types';

// Dedupe by id: guards against a paginated append repeating a classroom (e.g. a double-fired scroll).
const dedupeById = (classrooms: ClassroomModelV1[]): ClassroomModelV1[] => {
  const seen = new Set<string>();
  return classrooms.filter((classroom) => {
    if (seen.has(classroom.id)) {
      return false;
    }
    seen.add(classroom.id);
    return true;
  });
};

interface ClassesDropdownProps {
  selected: SelectedClassrooms;
  onChange: (selected: SelectedClassrooms) => void;
}

export const ClassesDropdown = ({selected, onChange}: ClassesDropdownProps) => {
  const [activeClasses, setActiveClasses] = useState<ClassroomModelV1[]>([]);
  const [activePage, setActivePage] = useState(1);
  const [activeTotalPages, setActiveTotalPages] = useState(0);

  // Archived classrooms are a separate query — fetched only once opted in via the toggle
  const [archivedClasses, setArchivedClasses] = useState<ClassroomModelV1[]>([]);
  const [archivedPage, setArchivedPage] = useState(1);
  const [archivedTotalPages, setArchivedTotalPages] = useState(0);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Synchronous re-entry guard: `isLoading` is async state, so two scroll events in one frame could
  // both pass an `isLoading` check and double-fetch the same page. A ref flips synchronously.
  const inFlightRef = useRef(false);

  const loadPage = useCallback(async (pageNum: number, archivedOnly: boolean) => {
    inFlightRef.current = true;
    try {
      setIsLoading(true);
      const query = getClassesQuery({
        archivedOnly,
        isTeacher: !sessionStore.isSchoolAdministrator(),
        page: pageNum
      });
      const results = await query.getResourcePromise();
      return {
        items: results.toArray() as ClassroomModelV1[],
        totalPages: query.getResourceMetadata().getIn(['page', 'total_pages'], 0) as number
      };
    } catch (error) {
      addToast({
        color: 'negative',
        title: 'Error fetching classes',
        message: normalizeError(error).message
      });
      return null;
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  }, []);

  const fetchActive = useCallback(
    async (pageNum: number) => {
      const result = await loadPage(pageNum, false);
      if (!result) return;
      setActiveTotalPages(result.totalPages);
      setActiveClasses((prev) =>
        pageNum === 1 ? result.items : dedupeById([...prev, ...result.items])
      );
    },
    [loadPage]
  );

  const fetchArchived = useCallback(
    async (pageNum: number) => {
      const result = await loadPage(pageNum, true);
      if (!result) return;
      setArchivedTotalPages(result.totalPages);
      setArchivedClasses((prev) =>
        pageNum === 1 ? result.items : dedupeById([...prev, ...result.items])
      );
    },
    [loadPage]
  );

  useEffect(() => {
    // oxlint-disable-next-line react/react-compiler -- effect intentionally kicks off a data fetch; the synchronous setState publishes its loading/reset state
    fetchActive(1);
  }, [fetchActive]);

  const toggleShowArchived = () => {
    // Fetch archived the first time it's revealed; the active list is left untouched.
    if (!showArchived && !archivedLoaded) {
      setArchivedLoaded(true);
      fetchArchived(1);
    }
    setShowArchived((prev) => !prev);
  };

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const {scrollHeight, scrollTop, clientHeight} = e.currentTarget;
      if (inFlightRef.current || scrollHeight - clientHeight - scrollTop >= 100) {
        return;
      }
      // Page through active classrooms first; only reach for archived once active is exhausted.
      if (activePage < activeTotalPages) {
        setActivePage((prev) => prev + 1);
        fetchActive(activePage + 1);
      } else if (showArchived && archivedPage < archivedTotalPages) {
        setArchivedPage((prev) => prev + 1);
        fetchArchived(archivedPage + 1);
      }
    },
    [
      activePage,
      activeTotalPages,
      showArchived,
      archivedPage,
      archivedTotalPages,
      fetchActive,
      fetchArchived
    ]
  );

  const toggleClassroom = (classroom: ClassroomModelV1) => {
    const next = {...selected};
    if (next[classroom.id]) {
      delete next[classroom.id];
    } else {
      next[classroom.id] = classroom.name;
    }
    onChange(next);
  };

  const noneSelected = Object.keys(selected).length === 0;

  return (
    <Menu position='bottom-end' positionFixed>
      <Menu.Trigger>
        {(triggerProps) => (
          <Button
            {...triggerProps}
            color='secondary'
            variant='outlined'
            data-testid='classes-trigger'
          >
            <Text color='inherit'>Classes</Text>
            <Icon icon='caret-down' className='ml-x1' />
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Content>
        <div className='max-h-[320px] overflow-y-auto' onScroll={handleScroll}>
          <Menu.Item
            selected={noneSelected}
            rightIconProps={noneSelected ? {icon: 'check'} : undefined}
            onClick={() => onChange({})}
          >
            All
          </Menu.Item>
          <Menu.Divider />
          {activeClasses.map((classroom) => (
            <Menu.Item
              key={classroom.id}
              selected={Boolean(selected[classroom.id])}
              rightIconProps={selected[classroom.id] ? {icon: 'check'} : undefined}
              onClick={() => toggleClassroom(classroom)}
            >
              {classroom.name}
            </Menu.Item>
          ))}
          {showArchived && archivedClasses.length > 0 && (
            <>
              <Menu.Divider />
              {archivedClasses.map((classroom) => (
                <Menu.Item
                  key={classroom.id}
                  selected={Boolean(selected[classroom.id])}
                  leftIconProps={{icon: 'box-archive'}}
                  rightIconProps={selected[classroom.id] ? {icon: 'check'} : undefined}
                  textProps={{color: 'secondary'}}
                  onClick={() => toggleClassroom(classroom)}
                >
                  {classroom.name}
                </Menu.Item>
              ))}
            </>
          )}
          {isLoading && (
            <div className='p-x2 flex justify-center'>
              <LoadingSpinner size={2} />
            </div>
          )}
        </div>
        <div className='gap-x1 border-border-subtle p-x1-half flex items-center justify-end border-t'>
          <Text size='s' color='secondary'>
            Show Archived
          </Text>
          <Toggle checked={showArchived} onChange={toggleShowArchived} />
        </div>
      </Menu.Content>
    </Menu>
  );
};
